import { useState, useEffect, useCallback } from 'react'
import { useSsoToken } from './useSsoToken'
import { getFeatureFlags } from '../services/claimsApi'
import { getApiErrorMessage } from '../utils/errorMessages'

// Backend feature flags, fetched on mount. App is the only consumer, so this is one
// request per add-in session — there is no shared cache, and a second call site would
// mean a second request.
//
// Fail-closed: while the request is in flight, and on any failure (auth, network,
// backend down), every flag reads as off. A gated feature stays hidden unless the
// backend explicitly enables it, so it can never flash on and then disappear.
//
// A failure is not retried here — it is reported through `errorMessage` so App can show
// the user a Retry button, the same way it does for a detection failure. `retry` re-runs
// the request on demand; nothing happens automatically.
export function useFeatureFlags() {
  const { getToken } = useSsoToken()
  const [flags, setFlags] = useState<Record<string, boolean>>({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const load = useCallback(async () => {
    setErrorMessage(null)
    try {
      const token = await getToken()
      const result = await getFeatureFlags(token)
      // This backend serializes an empty collection as null (see getClaimParticipants),
      // and a null here would throw on the flag lookup below — there is no error
      // boundary, so that would take down the whole task pane.
      setFlags(result ?? {})
    } catch (err: unknown) {
      console.error('[FeatureFlags] Failed to load:', err)
      setErrorMessage(getApiErrorMessage(err, 'Failed to load settings. Please try again.'))
    }
  }, [getToken])

  useEffect(() => {
    // Next tick, so the load never calls setState directly inside the effect body.
    void Promise.resolve().then(load)
  }, [load])

  return {
    enableContactsTab: flags.EnableContactsTab === true,
    enableTaskTab: flags.EnableTaskTab === true,
    enableTimeEntry: flags.EnableTimeEntry === true,
    enableFileNote: flags.EnableFileNote === true,
    enableSimpleAttach: flags.EnableSimpleAttach === true,
    errorMessage,
    retry: load,
  }
}
